/* eslint-disable react/prop-types */
import { message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { MicrophoneIcon } from './styled';
import { rootAPI } from '../../config/api';
import { recordWidgetAudio } from '../../utility/recordWidgetAudio';

const VoiceMessage = ({ setRecording, onTranscript, language, disabled, stopSignal, setTranscribing = () => {} }) => {
  const capture = useRef(null);
  const controller = useRef(null);
  const mounted = useRef(true);
  const active = useRef(false);
  const [phase, setPhase] = useState('idle');
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); capture.current?.cancel(); };
  }, []);
  useEffect(() => { if (stopSignal) capture.current?.stop(); }, [stopSignal]);
  const reset = () => {
    active.current = false;
    capture.current = null;
    if (mounted.current) { setPhase('idle'); setRecording(false); setTranscribing(false); }
  };
  const fail = (error) => {
    if (mounted.current && error.name !== 'AbortError') {
      message.error(error.name === 'NotAllowedError' ? 'Please allow microphone access to send a voice message.' : error.message || 'Could not process your voice. Please try again.');
    }
    reset();
  };
  const start = async () => {
    if (phase === 'recording') { capture.current?.stop(); return; }
    if (disabled || active.current) return;
    active.current = true;
    controller.current = new AbortController();
    try {
      setPhase('starting');
      capture.current = await recordWidgetAudio({
        signal: controller.current.signal,
        onError: fail,
        onComplete: async (blob) => {
          if (!mounted.current) return;
          setRecording(false);
          setTranscribing(true);
          setPhase('transcribing');
          try {
            if (!blob.size) throw new Error('No audio was recorded. Please try again.');
            const form = new FormData();
            form.append('audio', blob, blob.type.includes('mp4') ? 'recording.m4a' : 'recording.webm');
            form.append('language', language || 'auto');
            const timeout = setTimeout(() => controller.current?.abort(), 30000);
            let data;
            try {
              const response = await fetch(`${rootAPI}/api/conversations/v1/speech-to-text`, { method: 'POST', body: form, signal: controller.current.signal });
              if (!response.ok) throw new Error('Voice transcription is temporarily unavailable. Please try again.');
              data = await response.json();
            } finally { clearTimeout(timeout); }
            const text = typeof data.transcript === 'string' ? data.transcript : data.transcript?.transcription;
            if (!text?.trim()) throw new Error('I could not hear that clearly. Please try again.');
            if (!mounted.current) return;
            setTranscribing(false);
            setPhase('sending');
            // onTranscript inserts the customer message synchronously, before waiting for the AI.
            const submission = onTranscript(text.trim(), data.detectedLanguage || data.transcript?.detectedLanguage);
            setRecording(false);
            await submission;
          } catch (error) {
            if (mounted.current && error.name === 'AbortError') message.error('Voice processing timed out. Please try again.');
            else fail(error);
          } finally { reset(); }
        },
      });
      if (mounted.current && capture.current) { setPhase('recording'); setRecording(true); }
    } catch (error) { fail(error); }
  };
  return (
    <button type="button" onClick={start} disabled={disabled || ['starting', 'transcribing', 'sending'].includes(phase)}
      aria-label={phase === 'recording' ? 'Stop voice recording' : phase === 'transcribing' ? 'Processing voice message' : 'Start voice recording'}
      aria-busy={phase === 'transcribing'} style={{ border: 0, padding: 0, background: 'transparent', display: 'inline-flex', opacity: disabled ? 0.5 : 1 }}>
      <MicrophoneIcon src={phase === 'recording' ? require('../../static/img/icon/mute.png') : require('../../static/img/icon/mic.png')} alt="" />
    </button>
  );
};
export default VoiceMessage;
